import { lazy, Suspense } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { ProtectedRoute } from "./components/ProtectedRoute"
import { Box, CircularProgress, CssBaseline, ThemeProvider, Typography } from "@mui/material"
import { ApolloProvider } from "@apollo/client"
import { graphQlClient } from "./config/graphql"
import { defaultTheme } from "./config/theme"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { ErrorBoundary } from "./components/ErrorBoundary"

const DashboardPage = lazy(() => import("./pages/DashboardPage").then(m => ({ default: m.DashboardPage })))
const LoginPage = lazy(() => import("./pages/LoginPage").then(m => ({ default: m.LoginPage })))
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })))
const InstitutionsPage = lazy(() => import("./pages/InstitutionsPage").then(m => ({ default: m.InstitutionsPage })))
const InstitutionAddPage = lazy(() => import("./pages/InstitutionAddPage").then(m => ({ default: m.InstitutionAddPage })))
const InstitutionEditPage = lazy(() => import("./pages/InstitutionEditPage").then(m => ({ default: m.InstitutionEditPage })))
const InstitutionUnitsPage = lazy(() => import("./pages/InstitutionUnitsPage").then(m => ({ default: m.InstitutionUnitsPage })))
const InstitutionUnitAddPage = lazy(() => import("./pages/InstitutionUnitAddPage").then(m => ({ default: m.InstitutionUnitAddPage })))
const InstitutionUnitEditPage = lazy(() => import("./pages/InstitutionUnitEditPage").then(m => ({ default: m.InstitutionUnitEditPage })))
const InstitutionCertificateAddPage = lazy(() => import("./pages/InstitutionCertificateAddPage").then(m => ({ default: m.InstitutionCertificateAddPage })))
const InstitutionCertificateEditPage = lazy(() => import("./pages/InstitutionCertificateEditPage").then(m => ({ default: m.InstitutionCertificateEditPage })))
const InstitutionCertificatePage = lazy(() => import("./pages/InstitutionCertificatePage").then(m => ({ default: m.InstitutionCertificatePage })))
const CompaniesPage = lazy(() => import("./pages/CompaniesPage").then(m => ({ default: m.CompaniesPage })))
const CompanyAddPage = lazy(() => import("./pages/CompanyAddPage").then(m => ({ default: m.CompanyAddPage })))
const CompanyEditPage = lazy(() => import("./pages/CompanyEditPage/CompanyEditPage").then(m => ({ default: m.CompanyEditPage })))
const RolesPage = lazy(() => import("./pages/RolesPage").then(m => ({ default: m.RolesPage })))
const ProgramsReport = lazy(() => import("./pages/ProgramsReport").then(m => ({ default: m.ProgramsReport })))
const RequestsReport = lazy(() => import("./pages/RequestsReport").then(m => ({ default: m.RequestsReport })))
const WorkerRankingPage = lazy(() => import("./pages/WorkerRankingPage").then(m => ({ default: m.WorkerRankingPage })))
const WithdrawsPage = lazy(() => import("./pages/WithdrawsPage").then(m => ({ default: m.WithdrawsPage })))
const NotifyPage = lazy(() => import("./pages/NotifyPage").then(m => ({ default: m.NotifyPage })))
const CampaignsPage = lazy(() => import("./pages/CampaignsPage").then(m => ({ default: m.CampaignsPage })))
const CampaignEditPage = lazy(() => import("./pages/CampaignEditPage/CampaignEditPage").then(m => ({ default: m.CampaignEditPage })))
const CampaignAddPage = lazy(() => import("./pages/CampaignAddPage").then(m => ({ default: m.CampaignAddPage })))
const WorkersPage = lazy(() => import("./pages/WorkersPage").then(m => ({ default: m.WorkersPage })))
const WorkersAddPage = lazy(() => import("./pages/WorkersAddPage").then(m => ({ default: m.WorkersAddPage })))
const WorkersEditPage = lazy(() => import("./pages/WorkersEditPage").then(m => ({ default: m.WorkersEditPage })))
const RequestWorkersPage = lazy(() => import("./pages/RequestWorkersPage").then(m => ({ default: m.RequestWorkersPage })))
const RequestsPage = lazy(() => import("./pages/RequestsPage/RequestsPage").then(m => ({ default: m.RequestsPage })))
const RequestEditPage = lazy(() => import("./pages/RequestEditPage/RequestEditPage").then(m => ({ default: m.RequestEditPage })))
const ExceptionsPage = lazy(() => import("./pages/ExceptionsPage/ExceptionsPage").then(m => ({ default: m.ExceptionsPage })))
const ExceptionAddPage = lazy(() => import("./pages/ExceptionAddPage/ExceptionAddPage").then(m => ({ default: m.ExceptionAddPage })))
const InvitationsPage = lazy(() => import("./pages/InvitationsPage/InvitationsPage").then(m => ({ default: m.InvitationsPage })))
const FeedbacksPage = lazy(() => import("./pages/FeedbacksPage/FeedbacksPage").then(m => ({ default: m.FeedbacksPage })))
const ReportPaidAmountsPage = lazy(() => import("./pages/ReportPaidAmountsPage").then(m => ({ default: m.ReportPaidAmountsPage })))
const JobReportsPage = lazy(() => import("./pages/JobReportsPage").then(m => ({ default: m.JobReportsPage })))
const CertificatesPage = lazy(() => import("./pages/CertificatesPage/CertificatesPage").then(m => ({ default: m.CertificatesPage })))
const CertificateAddPage = lazy(() => import("./pages/CertificateAddPage/CertificateAddPage").then(m => ({ default: m.CertificateAddPage })))
const CertificateEditPage = lazy(() => import("./pages/CertificateEditPage/CertificateEditPage").then(m => ({ default: m.CertificateEditPage })))
const JobsPage = lazy(() => import("./pages/JobsPage/JobsPage").then(m => ({ default: m.JobsPage })))
const JobAddPage = lazy(() => import("./pages/JobAddPage/JobAddPage").then(m => ({ default: m.JobAddPage })))
const JobEditPage = lazy(() => import("./pages/JobEditPage/JobEditPage").then(m => ({ default: m.JobEditPage })))
const TrainingsPage = lazy(() => import("./pages/TrainingsPage/TrainingsPage").then(m => ({ default: m.TrainingsPage })))
const TrainingAddPage = lazy(() => import("./pages/TrainingAddPage/TrainingAddPage").then(m => ({ default: m.TrainingAddPage })))
const TrainingEditPage = lazy(() => import("./pages/TrainingEditPage/TrainingEditPage").then(m => ({ default: m.TrainingEditPage })))
const AdministratorsPage = lazy(() => import("./pages/AdministratorsPage/AdministratorsPage").then(m => ({ default: m.AdministratorsPage })))
const AdministratorAddPage = lazy(() => import("./pages/AdministratorAddPage/AdministratorAddPage").then(m => ({ default: m.AdministratorAddPage })))
const AdministratorEditPage = lazy(() => import("./pages/AdministratorEditPage/AdministratorEditPage").then(m => ({ default: m.AdministratorEditPage })))
const TermsEditPage = lazy(() => import("./pages/TermsEditPage/TermsEditPage").then(m => ({ default: m.TermsEditPage })))
const ReasonsPage = lazy(() => import("./pages/ReasonsPage/ReasonsPage").then(m => ({ default: m.ReasonsPage })))
const ReasonAddPage = lazy(() => import("./pages/ReasonAddPage/ReasonAddPage").then(m => ({ default: m.ReasonAddPage })))
const ReasonEditPage = lazy(() => import("./pages/ReasonEditPage/ReasonEditPage").then(m => ({ default: m.ReasonEditPage })))
const CompanyUsersPage = lazy(() => import("./pages/CompanyUsersPage/CompanyUsersPage").then(m => ({ default: m.CompanyUsersPage })))
const CompanyStoresPage = lazy(() => import("./pages/CompanyStoresPage/CompanyStoresPage").then(m => ({ default: m.CompanyStoresPage })))
const CompanyStoreAddPage = lazy(() => import("./pages/CompanyStoreAddPage/CompanyStoreAddPage").then(m => ({ default: m.CompanyStoreAddPage })))
const CompanyStoreJobsPage = lazy(() => import("./pages/CompanyStoreJobsPage/CompanyStoreJobsPage").then(m => ({ default: m.CompanyStoreJobsPage })))
const CompanyStoreJobsAddPage = lazy(() => import("./pages/CompanyStoreJobsAddPage/CompanyStoreJobsAddPage").then(m => ({ default: m.CompanyStoreJobsAddPage })))
const CompanyStoreJobsEditPage = lazy(() => import("./pages/CompanyStoreJobsEditPage/CompanyStoreJobsEditPage").then(m => ({ default: m.CompanyStoreJobsEditPage })))
const CompanyStoreEditPage = lazy(() => import("./pages/CompanyStoreEditPage/CompanyStoreEditPage").then(m => ({ default: m.CompanyStoreEditPage })))
const CoursesPage = lazy(() => import("./pages/CoursesPage/CoursesPage").then(m => ({ default: m.CoursesPage })))
const CourseEditPage = lazy(() => import("./pages/CourseEditPage/CourseEditPage").then(m => ({ default: m.CourseEditPage })))
const CourseAddPage = lazy(() => import("./pages/CourseAddPage/CourseAddPage").then(m => ({ default: m.CourseAddPage })))
const CourseVideosPage = lazy(() => import("./pages/CourseVideosPage/CourseVideosPage").then(m => ({ default: m.CourseVideosPage })))
const WorkerPage = lazy(() => import("./pages/WorkerPage/WorkerPage").then(m => ({ default: m.WorkerPage })))
const CourseAddVideoPage = lazy(() => import("./pages/CourseAddVideoPage/CourseAddVideoPage").then(m => ({ default: m.CourseAddVideoPage })))
const CoursesQuestionsPage = lazy(() => import("./pages/CourseQuestionsPage/CourseQuestionsPage").then(m => ({ default: m.CoursesQuestionsPage })))
const CourseEditVideoPage = lazy(() => import("./pages/CourseEditVideoPage/CourseEditVideoPage").then(m => ({ default: m.CourseEditVideoPage })))
const CourseAddQuestionsPage = lazy(() => import("./pages/CourseAddQuestionsPage/CourseAddQuestionsPage").then(m => ({ default: m.CourseAddQuestionsPage })))
const CourseEditQuestionPage = lazy(() => import("./pages/CourseEditQuestionPage/CourseEditQuestionPage").then(m => ({ default: m.CourseEditQuestionPage })))
const CompanyUserAddPage = lazy(() => import("./pages/CompanyUserAddPage/CompanyUserAddPage").then(m => ({ default: m.CompanyUserAddPage })))
const CompanyUserEditPage = lazy(() => import("./pages/CompanyUserEditPage/CompanyUserEditPage").then(m => ({ default: m.CompanyUserEditPage })))
const RequestWorkersEditPage = lazy(() => import("./pages/RequestWorkersEditPage/RequestWorkersEditPage").then(m => ({ default: m.RequestWorkersEditPage })))
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage").then(m => ({ default: m.AnalyticsPage })))

export default function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ApolloProvider client={graphQlClient}>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <ErrorBoundary>
          <BrowserRouter>
            <Suspense
              fallback={
                <Box
                  role="status"
                  aria-live="polite"
                  sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <CircularProgress aria-label="Carregando aplicação" />
                  <Typography color="text.secondary" variant="body2">
                    Carregando...
                  </Typography>
                </Box>
              }
            >
            <Routes>
              <Route element={<ProtectedRoute />}>
                <Route path="/" element={<DashboardPage />} />

                {/* Institutions */}
                <Route path="/institutions" element={<InstitutionsPage />} />
                <Route path="/institutions/add" element={<InstitutionAddPage />} />
                <Route path="/institutions/edit/:institutionId" element={<InstitutionEditPage />} />
                <Route path="/institutions/:institutionId/units" element={<InstitutionUnitsPage />} />
                <Route path="/institutions/:institutionId/units/add" element={<InstitutionUnitAddPage />} />
                <Route path="/institutions/:institutionId/units/edit/:unitId" element={<InstitutionUnitEditPage />} />
                <Route path="/institutions/:institutionId/certificate" element={<InstitutionCertificatePage />} />
                <Route path="/institutions/:institutionId/certificate/add" element={<InstitutionCertificateAddPage />} />
                <Route path="/institutions/:institutionId/certificate/edit/:certificateId" element={<InstitutionCertificateEditPage />} />

                {/* Certificates */}
                <Route path="/certificates" element={<CertificatesPage />} />
                <Route path="/certificates/add" element={<CertificateAddPage />} />
                <Route path="/certificates/edit/:certificateId" element={<CertificateEditPage />} />

                {/* Jobs */}
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/jobs/add" element={<JobAddPage />} />
                <Route path="/jobs/edit/:jobId" element={<JobEditPage />} />

                {/* Trainings */}
                <Route path="/trainings" element={<TrainingsPage />} />
                <Route path="/trainings/add" element={<TrainingAddPage />} />
                <Route path="/trainings/edit/:trainingId" element={<TrainingEditPage />} />

                {/* Companies */}
                <Route path="/companies" element={<CompaniesPage />} />
                <Route path="/companies/add" element={<CompanyAddPage />} />
                <Route path="/companies/edit/:companyId" element={<CompanyEditPage />} />
                <Route path="/companies/:companyId/users" element={<CompanyUsersPage />} />
                <Route path="/companies/:companyId/users/add" element={<CompanyUserAddPage />} />
                <Route path="/companies/:companyId/users/edit/:userId" element={<CompanyUserEditPage />} />
                <Route path="/companies/:companyId/stores" element={<CompanyStoresPage />} />
                <Route path="/companies/:companyId/stores/add" element={<CompanyStoreAddPage />} />
                <Route path="/companies/:companyId/stores/edit/:storeId" element={<CompanyStoreEditPage />} />
                <Route path="/companies/:companyId/stores/:storeId/functions" element={<CompanyStoreJobsPage />} />
                <Route path="/companies/:companyId/stores/:storeId/functions/add" element={<CompanyStoreJobsAddPage />} />
                <Route path="/companies/:companyId/stores/:storeId/functions/edit/:functionId" element={<CompanyStoreJobsEditPage />} />


                {/* Workers  */}
                <Route path="/workers" element={<WorkersPage />} />
                <Route path="/workers/add" element={<WorkersAddPage />} />
                <Route path="/workers/edit/:workerId" element={<WorkersEditPage />} />
                <Route path="/workers/:workerId" element={<WorkerPage />} />

                {/* Ranking */}
                <Route path="/ranking" element={<WorkerRankingPage />} />

                {/* Request Workers */}
                <Route path="/request-workers" element={<RequestWorkersPage />} />
                <Route path="/request-workers/edit/:requestWorkerId" element={<RequestWorkersEditPage />} />

                {/* Programs */}
                <Route path="/programs/report" element={<ProgramsReport />} />

                {/* Campigns */}
                <Route path="/campaigns" element={<CampaignsPage />} />
                <Route path="/campaigns/add" element={<CampaignAddPage />} />
                <Route path="/campaigns/edit/:campaignId" element={<CampaignEditPage />} />

                {/* Requests */}
                <Route path="/requests" element={<RequestsPage />} />
                <Route path="/requests/report" element={<RequestsReport />} />
                <Route path="/requests/edit/:requestId" element={<RequestEditPage />} />

                {/* Exceptions */}
                <Route path="/store_exceptions" element={<ExceptionsPage />} />
                <Route path="/exceptions/add" element={<ExceptionAddPage />} />

                {/* Invitations */}
                <Route path="/invitations" element={<InvitationsPage />} />

                {/* Feedbacks */}
                <Route path="/feedbacks" element={<FeedbacksPage />} />

                {/* Withdraws */}
                <Route path="/withdraws" element={<WithdrawsPage />} />

                {/* Report Paid Amounts */}
                <Route path="/report-paid-amounts" element={<ReportPaidAmountsPage />} />

                {/* Job Reports */}
                <Route path="/job/reports" element={<JobReportsPage />} />

                {/* Analytics */}
                <Route path="/analytics" element={<Navigate to="/analytics/users" replace />} />
                <Route path="/analytics/:section" element={<AnalyticsPage />} />
                <Route path="/analytics/ocr/logs" element={<AnalyticsPage />} />

                {/* Administrators */}
                <Route path="/administrators" element={<AdministratorsPage />} />
                <Route path="/administrators/add" element={<AdministratorAddPage />} />
                <Route path="/administrators/edit/:administratorId" element={<AdministratorEditPage />} />

                {/* Roles */}
                <Route path="/roles" element={<RolesPage />} />

                {/* Terms */}
                <Route path="/terms" element={<TermsEditPage />} />

                {/* Notify */}
                <Route path="/notify" element={<NotifyPage />}/>

                {/* Reasons */}
                <Route path="/reasons" element={<ReasonsPage />} />
                <Route path="/reasons/add" element={<ReasonAddPage />} />
                <Route path="/reasons/edit/:reasonId" element={<ReasonEditPage />} />

                {/* Courses */}
                <Route path="/courses" element={<CoursesPage />} />
                <Route path="/courses/edit/:courseId" element={<CourseEditPage />} />
                <Route path="/courses/add" element={<CourseAddPage />} />
                <Route path="/courses/videos/:courseId" element={<CourseVideosPage />} />
                <Route path="/courses/videos/:courseId/add" element={<CourseAddVideoPage />} />
                <Route path="/courses/videos/:courseId/edit/:videoId" element={<CourseEditVideoPage />} />
                <Route path="/courses/questions/:courseId" element={<CoursesQuestionsPage />} />
                <Route path="/courses/questions/:courseId/add" element={<CourseAddQuestionsPage />} />
                <Route path="/courses/questions/:courseId/edit/:questionId" element={<CourseEditQuestionPage />} />
              </Route>
              <Route element={<ProtectedRoute isLoginRoute />}>
                <Route path="/login" element={<LoginPage />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
          </ErrorBoundary>
        </ThemeProvider>
      </ApolloProvider>
    </LocalizationProvider>
  )
}
